<template>
  <model-form
    ref="modelForm"
    :model="voucherType"
    modal-type="modal"
    width="700px"
    show-translation-notice
    @on-show="resetTab"
  >
    <q-tabs
      :model-value="tab"
      align="justify"
      dense
      @update:model-value="onTabChange"
    >
      <q-tab
        name="details"
        label="Details"
      />
      <q-tab
        name="style"
        label="Style"
      />
      <q-tab
        name="card"
        label="Card"
      />
      <q-tab
        name="redemption"
        label="Redemption"
      />
      <q-tab
        name="restrictions"
        label="Restrictions"
      />
      <q-tab
        v-if="shouldShowReportingTab"
        name="reporting"
        label="Reporting"
      />
    </q-tabs>
    <div class="row q-col-gutter-sm items-start q-pt-md q-px-md">
      <model-form-input
        field="name"
        class="col-12"
        label="Name"
        :rules="[val => val !== null || 'Field is required']"
      />
    </div>
    <q-tab-panels v-model="tab">
      <q-tab-panel
        name="details"
        class="q-pt-none"
      >
        <div class="column">
          <div class="text-h6 q-mb-sm q-mt-md">
            Value
          </div>
          <div class="row q-col-gutter-sm items-start">
            <q-select
              v-model="voucherType.type"
              class="col-12"
              label="Type"
              :options="voucherType.fieldOptions('type')"
              emit-value
              map-options
              :rules="[val => val !== null || 'Field is required']"
              bottom-slots
            />
            <q-select
              v-if="voucherType.type === VOUCHER_TYPE_TYPES.SPECIFIC_ITEM"
              v-model="voucherType.itemVariation"
              class="col-12"
              use-input
              hide-selected
              fill-input
              input-debounce="0"
              label="Item Variation"
              :options="filteredItemVariations"
              option-label="nameWithItem"
              map-options
              emit-value
              :rules="[val => val !== null || 'Field is required']"
              bottom-slots
              @filter="filterItemVariations"
            />
            <q-select
              v-else-if="voucherType.type === VOUCHER_TYPE_TYPES.ITEM_PACKAGE"
              v-model="voucherType.itemPackage"
              class="col-6"
              use-input
              hide-selected
              fill-input
              input-debounce="0"
              label="Item Package"
              option-label="name"
              :options="itemPackages.data"
              emit-value
              map-options
              :rules="[val => val !== null || 'Field is required']"
              bottom-slots
            />
            <q-select
              v-if="voucherType.type === VOUCHER_TYPE_TYPES.SPECIFIC_MEMBERSHIP"
              v-model="voucherType.membershipType"
              class="col-6"
              label="Membership Type"
              :options="membershipTypes.data"
              option-label="name"
              emit-value
              map-options
              :rules="[val => val !== null || 'Field is required']"
              bottom-slots
            />
            <q-select
              v-if="voucherType.type === VOUCHER_TYPE_TYPES.SPECIFIC_MEMBERSHIP"
              v-model="voucherType.membershipTerm"
              class="col-6"
              label="Membership Term"
              :options="[{ value: 1, label: 'Month' }, { value: 3, label: 'Quarter' }, { value: 6, label: 'Biannual' }, { value: 12, label: 'Annual' }]"
              emit-value
              map-options
              :rules="[val => val !== null || 'Field is required']"
              bottom-slots
            />
          </div>
          <div
            class="row q-col-gutter-sm items-center"
          >
            <q-toggle
              v-model="voucherType.hasMinPrice"
              class="col-6 q-pb-md"
              label="Has Min Price"
              bottom-slots
            >
              <q-tooltip>
                When redeeming the voucher, the price charged for the redeemed item will not be less than the given min price.
              </q-tooltip>
            </q-toggle>
            <q-input
              v-model.number="voucherType.minPrice"
              :disable="!voucherType.hasMinPrice"
              class="col-6"
              :label="'Min Price Charged on Redemption (' + (voucherType.type === VOUCHER_TYPE_TYPES.TICKET || voucherType.type === VOUCHER_TYPE_TYPES.ACTIVITY_TICKET ? currentSite.ticketTypeReportingSuffix : currentSite.itemReportingSuffix) + ')'"
              :prefix="currentCircuit.localeCurrencySymbol"
              type="number"
              step="0.25"
              :rules="[val => val !== null || 'Field is required']"
              bottom-slots
            />
            <q-toggle
              v-model="voucherType.hasMaxPrice"
              class="col-6 q-pb-md"
              label="Has Max Value"
              bottom-slots
            >
              <q-tooltip>
                When redeeming the voucher, the price charged for the redeemed item will not be reduced by more than the given max value.
              </q-tooltip>
            </q-toggle>
            <q-input
              v-model.number="voucherType.maxPrice"
              :disable="!voucherType.hasMaxPrice"
              class="col-6"
              :label="'Max Redemption Value (' + (voucherType.type === VOUCHER_TYPE_TYPES.TICKET || voucherType.type === VOUCHER_TYPE_TYPES.ACTIVITY_TICKET ? currentSite.ticketTypeReportingSuffix : currentSite.itemReportingSuffix) + ')'"
              :prefix="currentCircuit.localeCurrencySymbol"
              type="number"
              step="0.25"
              :rules="[val => val !== null || 'Field is required']"
              bottom-slots
            />

            <q-toggle
              v-model="voucherType.hasMaxPercent"
              class="col-6 q-pb-md"
              label="Has Max Percent"
              bottom-slots
            >
              <q-tooltip>
                When redeeming the voucher, the price charged for the redeemed item will not be reduced by more than the given max percent.
              </q-tooltip>
            </q-toggle>
            <model-form-input
              :disable="!voucherType.hasMaxPercent"
              field="maxPercentThousandths"
              :decode="val => val / 1000"
              :encode="val => val * 1000"
              class="col"
              label="Percent"
              suffix="%"
              type="number"
              step="0.01"
              :rules="[val => val !== null || 'Field is required']"
              bottom-slots
              @update:model-value="value => voucherType.maxPercentThousandths = value * 1000"
            />
          </div>

          <div>
            <div
              class="text-h6 q-mb-sm q-mt-md"
            >
              Tax
            </div>
            <div class="row">
              <q-toggle
                v-model="voucherType.taxable"
                class="col-4 q-pb-md"
                :label="$t('pricing.taxable')"
                bottom-slots
              />
              <q-select
                v-if="voucherType.taxable"
                v-model="voucherType.taxIds"
                class="col-8"
                :label="$t('pricing.tax')"
                stack-label
                :options="taxes.data"
                option-value="id"
                map-options
                emit-value
                multiple
                clearable
                :rules="[val => (!!val && val.length > 0) || 'Field is required']"
                bottom-slots
              >
                <template #selected-item="scope">
                  <q-chip
                    v-if="scope.opt.name"
                    removable
                    dense
                    :tabindex="scope.tabindex"
                    :label="scope.opt.name"
                    @remove="scope.removeAtIndex(scope.index)"
                  />
                </template>
                <template #option="scope">
                  <q-item
                    v-bind="scope.itemProps"
                    v-on="scope.itemEvents"
                  >
                    <q-item-section>
                      {{ scope.opt.name }}
                    </q-item-section>
                  </q-item>
                </template>
              </q-select>
            </div>
          </div>
          <div class="text-h6 q-mb-sm q-mt-md">
            Expiry
          </div>
          <div class="row q-col-gutter-sm items-center">
            <q-toggle
              v-model="voucherType.expiryOptions['has_expiry']"
              class="col-3 q-pb-md"
              label="Expire"
              bottom-slots
            />
            <q-input
              v-model.number="voucherType.expiryOptions['expiry_value']"
              class="col-5"
              label="Expires In"
              type="number"
              step="1"
              min="1"
              :disable="!voucherType.expiryOptions['has_expiry']"
              :rules="[val => val !== null && val > 0 || 'Field is required']"
              bottom-slots
            />
            <q-select
              v-model="voucherType.expiryOptions['expiry_term']"
              class="col-4"
              label="Expiry Term"
              :options="$mh.asOptions(VOUCHER_TYPE_EXPIRY_TERMS.ALL)"
              emit-value
              map-options
              :disable="!voucherType.expiryOptions['has_expiry']"
              :rules="[val => val !== null || 'Field is required']"
              bottom-slots
            />
          </div>
        </div>
      </q-tab-panel>
      <q-tab-panel
        name="style"
        class="q-pt-none"
      >
        <div class="column">
          <div class="row q-col-gutter-sm items-start">
            <model-form-input
              field="description"
              class="col-12"
              label="Description"
              type="textarea"
            />
          </div>
          <div class="text-h6 q-mb-sm q-mt-md">
            Image
          </div>
          <div class="row q-col-gutter-sm items-start">
            <div class="col-6 column q-col-gutter-sm">
              <image-uploader
                v-model="voucherType.image"
                class="col"
                min-height="150px"
                :image-url="voucherType.imageUrl"
                :model="voucherType"
              />
              <div class="col-auto row justify-center items-center">
                <q-btn
                  :disable="!voucherType.image"
                  :color="voucherType.color"
                  glossy
                  unelevated
                  outline
                  :style="'background-color: ' + voucherType.color + ' !important;'"
                  size="lg"
                  class="col"
                >
                  <div class="text-caption text-weight-light">
                    <div class="text-capitalize">
                      {{ voucherType.color }}
                    </div>
                  </div>
                  <q-menu
                    anchor="top left"
                    self="top left"
                  >
                    <q-color v-model="voucherType.color" />
                  </q-menu>
                </q-btn>
              </div>
            </div>
            <div class="col-6">
              <voucher-card
                :voucher-type="voucherType"
              />
            </div>
          </div>
        </div>
      </q-tab-panel>
      <q-tab-panel
        name="card"
        class="q-pt-none"
      >
        <div class="q-mt-xs row q-col-gutter-sm items-center">
          <q-toggle
            v-model="voucherType.barcodeOptions.require_assigned_card"
            class="col-12 q-pb-md"
            label="Require Assigned Card"
          >
            <q-tooltip>Require a card be assigned for vouchers prior to sale (register only). If sold in a voucher package, assigned a shared card will meet this requirement.</q-tooltip>
          </q-toggle>
        </div>
        <div class="text-h6 q-mb-sm q-mt-md">
          Barcode Requirements
        </div>
        <div class="row q-col-gutter-sm items-center">
          <q-toggle
            v-model="voucherType.barcodeOptions.require_card_uid_prefix_match"
            class="col-6 q-pb-md"
            label="Require Prefix Match"
          >
            <q-tooltip>Require the first characters of the assigned card match the configured barcode number prefix.</q-tooltip>
          </q-toggle>
          <q-input
            v-model="voucherType.barcodeOptions.card_uid_prefix"
            class="col-6 col"
            label="Barcode Prefix"
            bottom-slots
          >
            <q-tooltip>A static prefix included at the beginning of the barcode number used by all cards assigned to this voucher type.</q-tooltip>
          </q-input>
          <q-input
            v-model.number="voucherType.barcodeOptions.card_uid_increment_length"
            class="col-6 col"
            label="Barcode Increment Length"
            type="number"
            step="1"
            min="0"
            :rules="[val => val !== null && val >= 0 || 'Field cannot be below 0']"
            bottom-slots
          >
            <q-tooltip>Number of digits for an incremental number embedded in the card barcode number. Required for "consecutive" voucher package card assignments.</q-tooltip>
          </q-input>
          <q-input
            v-model.number="voucherType.barcodeOptions.card_uid_length"
            class="col-6 col"
            label="Barcode Length (Total)"
            type="number"
            step="1"
            min="8"
            :rules="[val => val !== null && val >= 8 || 'Field cannot be below 8']"
            bottom-slots
          >
            <q-tooltip>Length of the barcode number generated for card numbers.</q-tooltip>
          </q-input>
        </div>
      </q-tab-panel>
      <q-tab-panel
        name="redemption"
        class="q-pt-none"
      >
        <div class="q-mt-xs column">
          <div
            v-if="voucherType.type !== VOUCHER_TYPE_TYPES.SPECIFIC_MEMBERSHIP"
            class="row q-col-gutter-sm items-start"
          >
            <q-toggle
              v-model="voucherType.limitRedemptionsByShowingEnabled"
              class="col-6 q-pb-md"
              label="Limit Redemptions Per Showing Per User"
            >
              <q-tooltip>Limit the number of times this discount can be redeemed for a single showing by a user</q-tooltip>
            </q-toggle>
            <q-toggle
              v-model="voucherType.limitRedemptionsByShowingIncludesOverlapsEnabled"
              :disable="!voucherType.limitRedemptionsByShowingEnabled"
              class="col-6 q-pb-md"
              label="Limit Overlapping Showings"
            >
              <q-tooltip>Consider showings that overlap by 30 minutes or more to be the same showing when limiting redemptions</q-tooltip>
            </q-toggle>
            <q-input
              v-model.number="voucherType.showingRedemptionsLimit"
              :disable="!voucherType.limitRedemptionsByShowingEnabled"
              class="col-12"
              label="Redemptions Per Showing"
              type="number"
              step="1"
              :rules="[val => !!val || 'Field is required']"
              bottom-slots
            />
          </div>
          <div
            v-if="voucherType.type !== VOUCHER_TYPE_TYPES.SPECIFIC_MEMBERSHIP"
            class="row q-col-gutter-sm items-start"
          >
            <q-toggle
              v-model="voucherType.limitRedemptionsByActivitySessionEnabled"
              class="col-4 q-pb-md"
              label="Limit Redemptions Per Activity Session"
            >
              <q-tooltip>Limit the number of times this voucher type can be redeemed for a single activity session</q-tooltip>
            </q-toggle>
            <q-toggle
              v-model="voucherType.limitRedemptionsByActivitySessionAdmissionTimes"
              :disable="!voucherType.limitRedemptionsByActivitySessionEnabled"
              class="col-4 q-pb-md"
              label="Limit By Admission Times"
            >
              <q-tooltip>Consider activity session admissions with non-overlapping entry and exit times to be different activity sessions</q-tooltip>
            </q-toggle>
            <q-input
              v-model.number="voucherType.activitySessionRedemptionsLimit"
              :disable="!voucherType.limitRedemptionsByActivitySessionEnabled"
              class="col-4"
              label="Redemptions Per Activity Session"
              type="number"
              step="1"
              :rules="[val => !!val || 'Field is required']"
              bottom-slots
            />
          </div>
          <div class="row q-col-gutter-sm items-start">
            <q-toggle
              v-model="voucherType.isReward"
              class="col-6"
              label="Is Reward"
              bottom-slots
            />
            <q-input
              v-model.number="voucherType.pointsCost"
              :disable="!voucherType.isReward"
              class="col"
              label="Points Cost"
              type="number"
              step="25"
              :rules="[val => val !== null || 'Field is required']"
              bottom-slots
            />
          </div>

          <div class="row q-col-gutter-sm items-start">
            <q-toggle
              v-model="voucherType.forSale"
              class="col-6"
              label="For Sale"
              bottom-slots
            />
            <q-input
              v-model.number="voucherType.price"
              :disable="!voucherType.forSale"
              class="col"
              label="Price"
              :prefix="currentCircuit.localeCurrencySymbol"
              type="number"
              step="0.25"
              :rules="[val => val !== null || 'Field is required']"
              bottom-slots
            />
          </div>
          <div class="row q-col-gutter-sm items-start">
            <q-toggle
              v-model="voucherType.serviceIssue"
              class="col-12"
              label="Service Issue (available to issue for free due to a customer service problem, e.g. canceled show)"
              bottom-slots
            />
          </div>
        </div>
      </q-tab-panel>
      <q-tab-panel
        name="restrictions"
        class="q-pt-none"
      >
        <div class="column">
          <div
            class="row q-col-sm items-start"
          >
            <voucher-type-preview :voucher-type="voucherType" />
          </div>
          <div
            class="text-h6 q-mb-sm q-mt-md"
          >
            Restrictions
          </div>

          <div class="row q-col-gutter-sm items-start">
            <q-toggle
              v-model="voucherType.membershipTypeRestrictionsEnabled"
              label="Restrict by Membership Type"
              class="col"
            />
            <div class="col">
              <q-icon name="info" />
              <span class="text-subtitle1">This setting is useful when wanting to restrict a voucher to specific membership types.</span>
              <span class="text-caption"><br>e.g. You may want to give your "premium" members the ability to use their points with this voucher type, but not "free" users.</span>
            </div>
          </div>
          <div class="row q-my-md q-gutter-sm items-start">
            <q-select
              v-model="voucherType.membershipTypeIds"
              class="col"
              :label="`${voucherType.membershipTypeRestrictionsExclude ? 'Exclude' : 'Include'} Membership Types`"
              :options="membershipTypes.data"
              option-label="name"
              option-value="id"
              map-options
              emit-value
              multiple
              use-chips
              :disable="!voucherType.membershipTypeRestrictionsEnabled"
            />
            <q-checkbox
              v-model="voucherType.membershipTypeRestrictionsExclude"
              class="col-auto"
              label="Exclude"
              :disable="!voucherType.membershipTypeRestrictionsEnabled"
            />
          </div>

          <div v-if="voucherType.type === VOUCHER_TYPE_TYPES.TICKET || voucherType.type === VOUCHER_TYPE_TYPES.ACTIVITY_TICKET">
            <div
              class="row q-gutter-sm items-start"
            >
              <q-select
                v-model="voucherType.siteTicketTypeIds"
                :label="(voucherType.excludeTicketTypes ? 'Exclude' : 'Include') + ' Ticket Types'"
                outlined
                class="col"
                multiple
                clearable
                :options="ticketTypeOptions"
                stack-label
                bottom-slots
                map-options
                emit-value
                use-input
                option-value="id"
                menu-anchor="bottom left"
                @filter="ticketTypeFilterFunction"
              >
                <template #selected-item="scope">
                  <q-chip
                    v-if="scope.opt.description"
                    :key="scope.opt.id"
                    removable
                    dense
                    style="max-width: 330px;"
                    :tabindex="scope.tabindex"
                    :label="scope.opt.description"
                    @remove="scope.removeAtIndex(scope.index)"
                  >
                    <q-tooltip>
                      {{ scope.opt.description }}
                    </q-tooltip>
                  </q-chip>
                </template>
                <template #option="scope">
                  <q-item
                    v-bind="scope.itemProps"
                    v-on="scope.itemEvents"
                  >
                    <q-item-section>
                      {{ scope.opt.description }}
                    </q-item-section>
                  </q-item>
                </template>
              </q-select>
              <q-checkbox
                v-model="voucherType.excludeTicketTypes"
                class="col-auto"
                label="Exclude"
              />
            </div>
          </div>

          <div
            v-if="voucherType.type === VOUCHER_TYPE_TYPES.TICKET"
            class="row q-gutter-sm items-start"
          >
            <q-select
              v-model="voucherType.titleClassIds"
              class="col"
              :label="`${(voucherType.excludeTitleClasses ? 'Exclude' : 'Include')} Title Classes`"
              stack-label
              :options="$_.unionBy(voucherType.titleClasses.data, titleClasses.data, 'id')"
              option-value="id"
              map-options
              emit-value
              multiple
              clearable
              outlined
              :disable="titleClassRestrictionsDisabled"
              bottom-slots
            >
              <template #selected-item="scope">
                <q-chip
                  v-if="scope.opt.name"
                  removable
                  dense
                  :tabindex="scope.tabindex"
                  :label="scope.opt.name"
                  @remove="scope.removeAtIndex(scope.index)"
                />
              </template>
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                  v-on="scope.itemEvents"
                >
                  <q-item-section>
                    {{ scope.opt.name }}
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
            <q-checkbox
              v-model="voucherType.excludeTitleClasses"
              label="Exclude"
              :disable="titleClassRestrictionsDisabled"
            />
          </div>
          <div
            v-if="voucherType.type === VOUCHER_TYPE_TYPES.TICKET"
            class="row q-gutter-sm items-start"
          >
            <q-select
              v-model="voucherType.siteMovieIds"
              class="col"
              :label="`${(voucherType.excludeMovies ? 'Exclude' : 'Include')} Movies`"
              stack-label
              :options="$_.unionBy(voucherType.siteMovies.data, movies.data, 'id')"
              option-value="id"
              map-options
              emit-value
              multiple
              clearable
              :use-input="true"
              input-style="search"
              input-debounce="100"
              outlined
              bottom-slots
              :disable="movieRestrictionsDisabled"
              @filter="filterMovies"
            >
              <template #selected-item="scope">
                <q-chip
                  v-if="scope.opt.name"
                  removable
                  dense
                  :tabindex="scope.tabindex"
                  :label="scope.opt.name"
                  @remove="scope.removeAtIndex(scope.index)"
                />
              </template>
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                  v-on="scope.itemEvents"
                >
                  <q-item-section>
                    {{ scope.opt.name }}
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
            <q-checkbox
              v-model="voucherType.excludeMovies"
              label="Exclude"
              :disable="movieRestrictionsDisabled"
            />
          </div>
          <div
            v-if="voucherType.type === VOUCHER_TYPE_TYPES.TICKET"
            class="row q-col-sm items-start"
          >
            <q-select
              v-model="voucherType.siteShowingIds"
              class="col"
              :label="`${(voucherType.excludeShowings ? 'Exclude' : 'Include')} Showings`"
              stack-label
              :options="$_.unionBy(voucherType.siteShowings.data, showings.data, 'id')"
              option-value="id"
              map-options
              emit-value
              multiple
              clearable
              :use-input="true"
              input-style="search"
              input-debounce="300"
              outlined
              bottom-slots
              @filter="filterShowings"
            >
              <template #selected-item="scope">
                <q-chip
                  v-if="scope.opt.movie"
                  removable
                  dense
                  :tabindex="scope.tabindex"
                  :label="scope.opt.movie.name + ' - ' + scope.opt.siteTime"
                  style="max-width: 280px;"
                  @remove="scope.removeAtIndex(scope.index)"
                >
                  <q-tooltip>{{ scope.opt.movie.name + ' - ' + scope.opt.siteTime }}</q-tooltip>
                </q-chip>
              </template>
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                  v-on="scope.itemEvents"
                >
                  <q-item-section>
                    {{ scope.opt.movie.name }} - {{ scope.opt.siteTime }}
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
            <q-checkbox
              v-model="voucherType.excludeShowings"
              label="Exclude"
            />
          </div>

          <div
            v-if="voucherType.type === VOUCHER_TYPE_TYPES.ACTIVITY_TICKET"
            class="row q-gutter-sm items-start"
          >
            <q-select
              v-model="voucherType.siteActivityIds"
              class="col"
              :label="`${(voucherType.excludeActivities ? 'Exclude' : 'Include')} Activities`"
              stack-label
              :options="$_.unionBy(voucherType.siteActivities.data, activities.data, 'id')"
              option-value="id"
              map-options
              emit-value
              multiple
              clearable
              :use-input="true"
              input-style="search"
              input-debounce="100"
              outlined
              bottom-slots
              :disable="activityRestrictionsDisabled"
              @filter="filterActivities"
            >
              <template #selected-item="scope">
                <q-chip
                  v-if="scope.opt.name"
                  removable
                  dense
                  :tabindex="scope.tabindex"
                  :label="scope.opt.name"
                  @remove="scope.removeAtIndex(scope.index)"
                />
              </template>
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                  v-on="scope.itemEvents"
                >
                  <q-item-section>
                    {{ scope.opt.name }}
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
            <q-checkbox
              v-model="voucherType.excludeActivities"
              label="Exclude"
              :disable="activityRestrictionsDisabled"
            />
          </div>

          <div
            v-if="voucherType.type === VOUCHER_TYPE_TYPES.ACTIVITY_TICKET"
            class="row q-col-sm items-start"
          >
            <q-select
              v-model="voucherType.siteActivitySessionIds"
              class="col"
              :label="`${(voucherType.excludeActivitySessions ? 'Exclude' : 'Include')} Activity Sessions`"
              stack-label
              :options="$_.unionBy(voucherType.siteActivitySessions.data, activitySessions.data, 'id')"
              option-value="id"
              map-options
              emit-value
              multiple
              clearable
              :use-input="true"
              input-style="search"
              input-debounce="300"
              outlined
              bottom-slots
              @filter="filterActivitySessions"
            >
              <template #selected-item="scope">
                <q-chip
                  v-if="scope.opt.activity"
                  removable
                  dense
                  :tabindex="scope.tabindex"
                  :label="scope.opt.activity.name + ' - ' + scope.opt.siteStartTime"
                  style="max-width: 280px;"
                  @remove="scope.removeAtIndex(scope.index)"
                >
                  <q-tooltip>{{ scope.opt.activity.name + ' - ' + scope.opt.siteStartTime }}</q-tooltip>
                </q-chip>
              </template>
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                  v-on="scope.itemEvents"
                >
                  <q-item-section>
                    {{ scope.opt.activity.name }} - {{ scope.opt.siteStartTime }}
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
            <q-checkbox
              v-model="voucherType.excludeActivitySessions"
              label="Exclude"
            />
          </div>

          <div
            v-if="voucherType.type === VOUCHER_TYPE_TYPES.ITEM"
            class="row q-col-sm items-start"
          >
            <q-select
              v-model="voucherType.itemClassIds"
              class="col"
              multiple
              clearable
              :options="$_.unionBy(voucherType.itemClasses.data, itemClasses.data, 'id')"
              stack-label
              bottom-slots
              map-options
              emit-value
              option-value="id"
              :label="`${(voucherType.excludeItemClasses ? 'Exclude' : 'Include')} Item Classes`"
            >
              <template #selected-item="scope">
                <q-chip
                  v-if="scope.opt.name"
                  removable
                  dense
                  :tabindex="scope.tabindex"
                  :label="scope.opt.name"
                  @remove="scope.removeAtIndex(scope.index)"
                />
              </template>
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                  v-on="scope.itemEvents"
                >
                  <q-item-section>
                    <q-item-label>
                      {{ scope.opt.name }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
            <q-checkbox
              v-model="voucherType.excludeItemClasses"
              label="Exclude"
            />
          </div>
          <div
            v-if="voucherType.type === VOUCHER_TYPE_TYPES.ITEM"
            class="row q-col-sm items-start"
          >
            <q-select
              v-model="voucherType.siteItemIds"
              class="col"
              :label="`${(voucherType.excludeItems ? 'Exclude' : 'Include')} Items`"
              stack-label
              :options="$_.unionBy(voucherType.siteItems.data, filteredItems, 'id')"
              option-value="id"
              map-options
              emit-value
              multiple
              clearable
              use-input
              input-style="search"
              input-debounce="100"
              outlined
              bottom-slots
              @filter="filterItems"
            >
              <template #selected-item="scope">
                <q-chip
                  v-if="scope.opt.name"
                  removable
                  dense
                  :tabindex="scope.tabindex"
                  :label="scope.opt.name"
                  @remove="scope.removeAtIndex(scope.index)"
                />
              </template>
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                  v-on="scope.itemEvents"
                >
                  <q-item-section>
                    <q-item-label>
                      {{ scope.opt.name }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
            <q-checkbox
              v-model="voucherType.excludeItems"
              label="Exclude"
            />
          </div>

          <div
            v-if="voucherType.type === VOUCHER_TYPE_TYPES.ITEM"
            class="row q-col-sm items-start"
          >
            <q-select
              v-model="voucherType.siteItemVariationIds"
              class="col"
              :label="(voucherType.excludeItemVariations ? 'Exclude' : 'Include') + ' Item Variations'"
              stack-label
              :options="$_.unionBy(voucherType.siteItemVariations.data, filteredItemVariations, 'id')"
              map-options
              emit-value
              option-label="nameWithItem"
              option-value="id"
              use-input
              input-style="search"
              input-debounce="100"
              multiple
              clearable
              bottom-slots
              @filter="filterItemVariations"
              @clear="voucherType.siteItemVariationIds = []"
            >
              <template #selected-item="scope">
                <q-chip
                  v-if="scope.opt.name"
                  :removable="!voucherType.isTranslation"
                  dense
                  :tabindex="scope.tabindex"
                  :label="scope.opt.nameWithItem"
                  @remove="scope.removeAtIndex(scope.index)"
                />
              </template>
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                  v-on="scope.itemEvents"
                >
                  <q-item-section>
                    <q-item-label>
                      {{ scope.opt.nameWithItem }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
            <model-form-checkbox
              field="excludeItemVariations"
              label="Exclude"
            />
          </div>

          <div class="row q-col-gutter-x-sm items-center">
            <model-form-select
              field="conditions"
              class="col-12 q-mt-md"
              label="Conditions"
              :options="$mh.asOptions(VOUCHER_TYPE_CONDITIONS.ALL)"
              map-options
              emit-value
              multiple
              use-chips
              clearable
              :decode="conditions => $mh.objectToArray(conditions)"
              :encode="enabledConditions => $mh.arrayToObject(enabledConditions || [], { allKeys: VOUCHER_TYPE_CONDITIONS.ALL })"
              :rules="[val => voucherType.isMeerkatMovieValue ? $_.includes(VOUCHER_TYPE_CONDITIONS.DAY_OF_WEEK, val) || 'Meerkat Movies vouchers must have a day of week condition' : null]"
            >
              <template #option="scope">
                <q-item
                  v-bind="scope.itemProps"
                  v-on="scope.itemEvents"
                >
                  <q-item-section>
                    <div class="row items-center justify-between q-gutter-x-sm">
                      <span>{{ scope.opt.label }}</span>
                      <q-icon
                        name="info"
                        size="sm"
                      >
                        <q-tooltip>
                          {{ VOUCHER_TYPE_CONDITIONS_TOOLTIPS[scope.opt.value] }}
                        </q-tooltip>
                      </q-icon>
                    </div>
                  </q-item-section>
                </q-item>
              </template>
              <q-tooltip>
                Set conditions to be met for members to redeem this voucher type. If multiple conditions are selected, all must be met for redemption.
              </q-tooltip>
            </model-form-select>
            <!-- Condition Requirements -->
            <dates-condition-form
              v-if="voucherType.conditions[VOUCHER_TYPE_CONDITIONS.DATES]"
              :conditional="voucherType"
            />

            <date-range-condition-form
              v-if="voucherType.conditions[VOUCHER_TYPE_CONDITIONS.DATE_RANGE]"
              :conditional="voucherType"
            />

            <day-of-week-condition-form
              v-if="voucherType.conditions[VOUCHER_TYPE_CONDITIONS.DAY_OF_WEEK]"
              :conditional="voucherType"
            />

            <sites-condition-form
              v-if="voucherType.conditions[VOUCHER_TYPE_CONDITIONS.SITES]"
              :conditional="voucherType"
            />

            <time-of-day-condition-form
              v-if="voucherType.conditions[VOUCHER_TYPE_CONDITIONS.TIME_OF_DAY]"
              :conditional="voucherType"
            />
          </div>
          <limit-by-quantity-form
            v-if="voucherType.isMeerkatMovieValue"
            :conditional="voucherType"
          />
        </div>
      </q-tab-panel>
      <q-tab-panel
        v-if="shouldShowReportingTab"
        name="reporting"
      >
        <div class="column">
          <div class="row q-col-gutter-sm items-start">
            <q-toggle
              v-model="voucherType.hasCustomReportingName"
              class="col-6"
              label="Has Reporting Name"
              bottom-slots
            >
              <q-tooltip>
                The reporting name used to group ticket types for internal reporting
              </q-tooltip>
            </q-toggle>
            <q-input
              v-model="voucherType.customReportingName"
              class="col-6"
              label="Reporting Name"
              :disable="!voucherType.hasCustomReportingName"
              bottom-slots
            >
              <q-tooltip>
                When used to redeem a ticket type, this will override the name of the ticket type in reporting, if prioritised in the ticket type reporting priorities
              </q-tooltip>
            </q-input>
          </div>
          <div class="row q-col-gutter-sm items-start">
            <q-toggle
              v-model="voucherType.hasMinReportedPrice"
              class="col-6"
              label="Has Minimum Reporting Value"
              bottom-slots
            />
            <q-input
              v-model.number="voucherType.minReportedPrice"
              :disable="!voucherType.hasMinReportedPrice"
              class="col"
              :label="'Minimum Reporting Value (' + currentSite.ticketTypeReportingSuffix + ')'"
              :prefix="currentCircuit.localeCurrencySymbol"
              type="number"
              step="0.25"
              :rules="[val => val !== null || 'Field is required']"
              bottom-slots
            />
            <q-tooltip>
              With this enabled, the ticket type price recorded in the BOR reporting will be at least the value specified.
            </q-tooltip>
          </div>
          <div class="row q-col-gutter-sm items-start">
            <q-toggle
              v-model="voucherType.hasMaxReportedPrice"
              class="col-6"
              label="Has Maximum Reporting Value"
              bottom-slots
            />
            <q-input
              v-model.number="voucherType.maxReportedPrice"
              :disable="!voucherType.hasMaxReportedPrice"
              class="col"
              :label="'Maximum Reporting Value (' + currentSite.ticketTypeReportingSuffix + ')'"
              :prefix="currentCircuit.localeCurrencySymbol"
              type="number"
              step="0.25"
              :rules="[val => val !== null || 'Field is required']"
              bottom-slots
            />
            <q-tooltip>
              With this enabled, the ticket type price recorded in the BOR reporting will be capped at the value specified.
            </q-tooltip>
          </div>
        </div>
      </q-tab-panel>
    </q-tab-panels>
  </model-form>
</template>

<script>
import { VOUCHER_TYPE_CONDITIONS, VOUCHER_TYPE_EXPIRY_TERMS, VOUCHER_TYPE_TYPES } from 'src/models/voucher/VoucherType'
import DateRangeConditionForm from 'src/modules/admin/components/condition-requirement-forms/DateRangeConditionForm'
import DatesConditionForm from 'src/modules/admin/components/condition-requirement-forms/DatesConditionForm'
import DayOfWeekConditionForm from 'src/modules/admin/components/condition-requirement-forms/DayOfWeekConditionForm'
import LimitByQuantityForm from 'src/modules/admin/components/condition-requirement-forms/LimitByQuantityForm'
import SitesConditionForm from 'src/modules/admin/components/condition-requirement-forms/SitesConditionForm'
import TimeOfDayConditionForm from 'src/modules/admin/components/condition-requirement-forms/TimeOfDayConditionForm'
import ImageUploader from 'src/modules/admin/components/ImageUploader'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'
import VoucherCard from 'src/modules/admin/voucher-type/components/VoucherCard'
import VoucherTypePreview from 'src/modules/admin/voucher-type/VoucherTypePreview'

const VOUCHER_TYPE_CONDITIONS_TOOLTIPS = {
  'day-of-week': 'Vouchers will be redeemable on the given day of the week.',
  'time-of-day': 'Vouchers will be redeemable during a given time range.',
  'date-range': 'Vouchers will be redeemable during a given date range.',
  dates: 'Vouchers will be redeemable for showings on the given dates.',
  sites: 'Vouchers will be redeemable on orders for the given sites.'
}

export default {
  name: 'VoucherTypeForm',
  components: {
    DateRangeConditionForm,
    DatesConditionForm,
    DayOfWeekConditionForm,
    ImageUploader,
    ModelForm,
    SitesConditionForm,
    TimeOfDayConditionForm,
    VoucherCard,
    VoucherTypePreview,
    LimitByQuantityForm
  },
  configuration: ['currentSite', 'currentCircuit', 'membershipTypes', 'itemClasses', 'items', 'itemVariations', 'titleClasses', 'ticketTypes', 'taxes', 'itemPackages'],
  models: {
    voucherType: {
      taxes: {
        fields: ['id', 'name']
      },
      itemClasses: {
        fields: ['id', 'name']
      },
      siteItems: {
        fields: ['id', 'name', 'itemClassId']
      },
      siteItemIds: true,
      siteItemVariations: {
        fields: ['id', 'name', 'itemId'],
        item: {
          fields: ['id', 'name']
        }
      },
      sites: {
        fields: ['id', 'name']
      },
      siteTicketTypes: {
        fields: ['id', 'name']
      },
      titleClasses: {
        fields: ['id', 'name']
      },
      siteMovies: {
        fields: ['id', 'name']
      },
      siteMovieIds: 'default',
      siteShowings: {
        fields: ['id', 'time'],
        movie: {
          fields: ['id', 'name'],
          titleClass: {
            fields: ['id', 'name']
          }
        }
      },
      siteShowingIds: 'default',
      siteActivities: {
        fields: ['id', 'name']
      },
      siteActivityIds: 'default',
      siteActivitySessions: {
        activity: 'default'
      },
      siteActivitySessionIds: 'default',
      itemVariations: {
        fields: ['id', 'name', 'itemId'],
        item: {
          fields: ['id', 'name']
        }
      },
      modelAudits: true,
      membershipTypes: {
        fields: ['id', 'name']
      }
    },
    movies: {
      fields: ['id', 'name'],
      variables () {
        return {
          searchString: this.movieSearchString,
          orderBy: 'createdAt',
          descending: true,
          limit: 5
        }
      }
    },
    showings: {
      fields: ['id', 'time'],
      movie: {
        fields: ['id', 'name']
      },
      variables () {
        return {
          siteId: this.currentSite?.id,
          searchString: this.showingSearchString,
          orderBy: 'time',
          period: 'future',
          limit: 5
        }
      }
    },
    activities: {
      fields: ['id', 'name'],
      variables () {
        return {
          searchString: this.activitySearchString,
          orderBy: 'createdAt',
          descending: true,
          limit: 5
        }
      }
    },
    activitySessions: {
      activity: 'default',
      variables () {
        return {
          searchString: this.activitySessionSearchString,
          orderBy: 'startTime',
          period: 'future',
          limit: 5
        }
      }
    }
  },
  data () {
    return {
      tab: 'details',
      ticketTypeSearchValue: null,
      itemSearchString: '',
      itemVariationSearchString: '',
      showingSearchString: '',
      movieSearchString: '',
      activitySearchString: '',
      activitySessionSearchString: '',
      currentBlockIndex: null,
      updateKey: 0,
      showStartTimeClock: false,
      showEndTimeClock: false,
      showExcludeDatesCalendar: false,
      excludeDateDate: null
    }
  },
  computed: {
    activeItems () {
      return _.filter(this.items.data, 'isUnarchived')
    },
    filteredItems () {
      return _.filter(this.activeItems, item => {
        if (!_.isEmpty(this.voucherType.itemClassIds) && this.voucherType.excludeItemClasses === _.includes(this.voucherType.itemClassIds, item.itemClassId)) {
          return false
        }

        return _.includes(item.name.toLowerCase(), this.itemSearchString)
      })
    },
    activeItemVariations () {
      return _.filter(this.itemVariations.data, 'isUnarchived')
    },
    filteredItemVariations () {
      return _.filter(this.activeItemVariations, itemVariation => {
        if (!_.isEmpty(this.voucherType.siteItemIds) && this.voucherType.excludeItems === _.includes(this.voucherType.siteItemIds, itemVariation.itemId)) {
          return false
        }

        return _.includes(itemVariation.nameWithItem.toLowerCase(), this.itemVariationSearchString)
      })
    },
    shouldShowReportingTab () {
      return (this.voucherType.type === VOUCHER_TYPE_TYPES.TICKET || this.voucherType.type === VOUCHER_TYPE_TYPES.ACTIVITY_TICKET) && this.$isPermitted(this.$permissions.OperationsReporting.View)
    },
    ticketTypeOptions () {
      const allOptions = _.filter(this.ticketTypes.data, 'isUnarchived')

      if (!this.ticketTypeSearchValue) return allOptions

      const searchTerms = this.ticketTypeSearchValue.toLowerCase().split(' ')

      const filteredValues = allOptions.filter(ticketType => {
        return _.every(searchTerms, term => {
          return ticketType.description.toLowerCase().includes(term)
        })
      })
      return filteredValues
    },
    movieRestrictionsDisabled () {
      return this.voucherType.excludeShowings || !!this.voucherType.showingIds?.length || !!this.voucherType.siteShowingIds?.length
    },
    activityRestrictionsDisabled () {
      return this.voucherType.excludeActivitySessions || !!this.voucherType.activitySessionIds?.length || !!this.voucherType.siteActivitySessionIds?.length
    },
    titleClassRestrictionsDisabled () {
      return this.voucherType.excludeShowings || !!this.voucherType.showingIds?.length || !!this.voucherType.siteShowingIds?.length || this.voucherType.excludeMovies || !!this.voucherType.movieIds?.length || !!this.voucherType.siteMovieIds?.length
    }
  },
  created () {
    _.extend(this, { VOUCHER_TYPE_CONDITIONS, VOUCHER_TYPE_CONDITIONS_TOOLTIPS, VOUCHER_TYPE_TYPES, VOUCHER_TYPE_EXPIRY_TERMS })
  },
  methods: {
    filterShowings (val, update, abort) {
      update(() => {
        this.showingSearchString = val
      })
    },
    filterMovies (val, update, abort) {
      update(() => {
        this.movieSearchString = val
      })
    },
    filterActivities (val, update, abort) {
      update(() => {
        this.activitySearchString = val
      })
    },
    filterActivitySessions (val, update, abort) {
      update(() => {
        this.activitySessionSearchString = val
      })
    },
    filterItems (val, update, abort) {
      update(() => {
        this.itemSearchString = val.toLowerCase()
      })
    },
    filterItemVariations (val, update, abort) {
      update(() => {
        this.itemVariationSearchString = val.toLowerCase()
      })
    },
    ticketTypeFilterFunction (val, update, abort) {
      update(() => {
        this.ticketTypeSearchValue = val.toLowerCase()
      })
    },
    resetTab () {
      this.tab = 'details'
    },
    onTabChange (tab) {
      this.$refs.modelForm.validateForm(
        () => { this.tab = tab },
        () => { this.$refs.modelForm.showErrorBanner() }
      )
    }
  }
}

</script>
